import React from 'react';
import useMobileLayout from 'Hooks/useMobileLayout';
import TableCell from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableCell';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getBorderRadius } from 'Components/utils';
import { dataAttrs } from 'Helpers/Testing';
import { tableCellStyle } from './ScenarioSelectionDetailedSummaryHelpers';

const useStyles = buildUseStyles('useDivideColumns', (theme) => ({
  contentColumn: {
    ...tableCellStyle(theme),
    textWrap: 'nowrap',
  },
  separatorColumn: {
    ...tableCellStyle(theme),
    background: 'transparent !important', // !important needed to override the emphasized class which has slightly higher specificity
    // Keep border radii correct by adding border radii to the left and right of this empty cell
    '& + td, & + th': getBorderRadius(theme.radius.md, { left: true }),
    'td:has(+ &), th:has(+ &)': getBorderRadius(theme.radius.md, { right: true }),
    // In dense mode there's no padding, so we need to make sure that this separator column takes up some space
    minWidth: theme.spacing(2),
  },
}));

const wrapInTableCells = (columnContent, classes) => columnContent.map(content => <TableCell align="right" className={classes.contentColumn} {...dataAttrs('non-sticky-column')}>{content}</TableCell>);

const addSeparatorColumn = (columnContent, classes) => {
  // Don't add a separator column if there's nothing to separate
  if (!columnContent || columnContent.length === 0) {
    return [];
  }
  // There's content here, so let's separate it visually from the sticky column
  return [<TableCell data-empty-column className={classes.separatorColumn} />, ...columnContent];
};

export const useDivideColumns = ({ allColumnContent, showMonths }) => {
  const isMobile = useMobileLayout();
  const classes = useStyles();
  // If we shouldn't show months, then there's no need to show anything but the first column
  const relevantColumns = showMonths ? allColumnContent : [allColumnContent[0]];
  // If you're on mobile and showing months, then the fact of the matter is, you're going to have to scroll.
  // If we make any of these columns sticky, we're letting the user see less info, and that's bad.
  // So make all columns not be sticky
  if (isMobile && showMonths) {
    return {
      stickyColumn: undefined,
      nonStickyColumns: addSeparatorColumn(wrapInTableCells(relevantColumns, classes), classes),
    };
  }
  return {
    stickyColumn: relevantColumns[0],
    nonStickyColumns: addSeparatorColumn(wrapInTableCells(relevantColumns.slice(1), classes), classes),
  };
};
